import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiRadio, FiWifi, FiWifiOff } from 'react-icons/fi';
import { apiGet } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Badge } from '@/shared/components/ui/Badge';
import type { ChannelItem } from '../types';
import { formatProviderLabel, channelStatusLabels } from '../constants';

interface ChannelsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type StatusFilter = 'all' | 'connected' | 'disconnected';

const formatRelative = (iso: string | undefined) => {
  if (!iso) return '—';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffH = diffMs / (1000 * 60 * 60);
  if (diffH < 1) return `há ${Math.max(1, Math.floor(diffMs / 60000))}min`;
  if (diffH < 24) return `há ${Math.floor(diffH)}h`;
  if (diffH < 48) return 'ontem';
  if (diffH < 24 * 7) return `${Math.floor(diffH / 24)}d atrás`;
  return date.toLocaleDateString('pt-BR');
};

const isConnected = (status: string) => status === 'active';

export const ChannelsDrawer: React.FC<ChannelsDrawerProps> = ({ isOpen, onClose }) => {
  const [filter, setFilter] = useState<StatusFilter>('all');

  const { data: items, isLoading } = useQuery({
    queryKey: ['admin-dashboard-channels'],
    queryFn: () => apiGet<ChannelItem[]>('/dashboard/channels-list'),
    enabled: isOpen,
  });

  const filtered = useMemo(() => {
    if (!items) return [];
    if (filter === 'all') return items;
    if (filter === 'connected') return items.filter(c => isConnected(c.status));
    return items.filter(c => !isConnected(c.status));
  }, [items, filter]);

  const counts = useMemo(() => {
    if (!items) return { all: 0, connected: 0, disconnected: 0 };
    const connected = items.filter(c => isConnected(c.status)).length;
    return { all: items.length, connected, disconnected: items.length - connected };
  }, [items]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      icon={<FiRadio className="w-5 h-5" />}
      title="Canais"
      subtitle={items ? `${counts.connected} conectado${counts.connected !== 1 ? 's' : ''} · ${counts.disconnected} desconectado${counts.disconnected !== 1 ? 's' : ''}` : undefined}
    >
      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b" style={{ borderColor: 'var(--theme-card-border)' }}>
        {([
          { key: 'all', label: `Todos (${counts.all})` },
          { key: 'connected', label: `Conectados (${counts.connected})` },
          { key: 'disconnected', label: `Desconectados (${counts.disconnected})` },
        ] as const).map(tab => {
          const active = filter === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className="py-3 px-4 text-sm font-medium relative transition-colors"
              style={{ color: active ? 'var(--theme-primary)' : 'var(--theme-muted-text)' }}
            >
              {tab.label}
              {active && (
                <span
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                />
              )}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={64} variant="rectangular" />
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((ch) => (
            <div
              key={ch.channelId}
              className="p-3 rounded-lg flex items-center gap-3"
              style={{ border: '1px solid var(--theme-card-border)' }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: isConnected(ch.status) ? '#dcfce7' : '#fee2e2',
                  color: isConnected(ch.status) ? '#16a34a' : '#dc2626',
                }}
              >
                {isConnected(ch.status) ? <FiWifi className="w-4 h-4" /> : <FiWifiOff className="w-4 h-4" />}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                    {ch.channelName}
                  </p>
                  {ch.engine && (
                    <Badge variant="outline" size="sm">
                      {ch.engine === 'node' ? 'Node' : 'Go'}
                    </Badge>
                  )}
                </div>
                <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                  {ch.companyName} · {formatProviderLabel(ch.providerId)} · {ch.identifier || '—'}
                </p>
              </div>

              <div className="text-right flex-shrink-0">
                <Badge variant={isConnected(ch.status) ? 'success' : 'error'}>
                  {channelStatusLabels[ch.status] || ch.status}
                </Badge>
                <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                  {formatRelative(ch.lastActivityAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <FiRadio className="w-12 h-12 mb-3" style={{ color: 'var(--theme-muted-text)', opacity: 0.5 }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhum canal nesta categoria
          </p>
        </div>
      )}
    </Drawer>
  );
};
